import { useCallback, useState } from 'react';
import { useQuota } from '../../components/quota/useQuota';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { GenerationRequestRead } from '../../types/api';
import { redesignRequest } from '../generation/generationApi';
import { MAX_REVISIONS } from './reviewLimits';

export interface RedesignFlow {
  isSubmitting: boolean;
  error: string | null;
  /** Revisions this lineage has already spent — the budget it shares. */
  used: number;
  isCapped: boolean;
  /** Sends the note and hands back the request, now `designing`. */
  submit: (feedback: string) => void;
  /** Clears the last message — the reviewer changed what they are asking for. */
  reset: () => void;
}

/** Message for the domain errors a redesign can provoke. */
function redesignMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'REVISION_CAP_REACHED':
        return `This post has used all ${MAX_REVISIONS} revisions. Edit the layers by hand — that is free — or regenerate it to start over.`;
      case 'REQUEST_NOT_EDITABLE':
        return 'This post is no longer waiting for your approval. Reload it to see where it stands.';
      case 'QUEUE_UNAVAILABLE':
        return 'We are not taking new work right now. Try again in a minute.';
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That redesign did not send. Try again.');
}

/**
 * The visual half of the feedback loop — Review Step 2 only.
 *
 * "The words are right, the picture is not" used to cost a whole new run: a
 * rejection threw away a caption the reviewer had just approved and bought
 * fresh research, two fresh drafts and fresh art to answer a complaint about a
 * layout. This asks for the one thing that was wrong: the design agent re-plans
 * the slides on the note, over the same caption and the backdrops this run
 * already drew, and the deck comes back to this screen.
 *
 * It answers 202 with the request row at `designing`, so the caller applies it
 * through the same path a revision and a selection take — the page falls back
 * to the stage tracker and the stream resubscribes on a fresh token.
 *
 * The budget is the revision budget, shared with {@link useReviseVariant}: five
 * per lineage, and never one of the three regeneration attempts.
 */
export function useRedesignSlides(
  businessId: string,
  requestId: string,
  /** `request.revisions` — what the lineage has spent so far. */
  used: number,
  onRedesigned: (next: GenerationRequestRead) => void,
): RedesignFlow {
  const { refresh: refreshQuota } = useQuota();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefused, setIsRefused] = useState(false);

  const reset = useCallback(() => setError(null), []);

  const submit = useCallback(
    (feedback: string) => {
      if (isSubmitting) return;

      setError(null);
      setIsSubmitting(true);

      void (async () => {
        try {
          const updated = await redesignRequest(businessId, requestId, { feedback });
          setIsSubmitting(false);
          // One design call is still a model call, so the header counter reads
          // the server rather than guessing what it cost.
          refreshQuota();
          onRedesigned(updated);
        } catch (caught) {
          setIsSubmitting(false);
          if (caught instanceof ApiError && caught.code === 'REVISION_CAP_REACHED') {
            setIsRefused(true);
          }
          setError(redesignMessage(caught));
        }
      })();
    },
    [businessId, isSubmitting, onRedesigned, refreshQuota, requestId],
  );

  return {
    isSubmitting,
    error,
    used,
    isCapped: isRefused || used >= MAX_REVISIONS,
    submit,
    reset,
  };
}
